import {SvgIcon, SvgIconProps} from "@mui/material";
import {lineBlueGrey, lineBlueLight, panelMetalGrey} from "../../const";

// Line-drawing icons for the fill configuration — the glazing options and the fixed
// panel's position. Each is a jamb in plan section: the filled frame profile on the
// left, the opening running off to the right, and the depth through the wall down
// the icon (outside face at the top, inside at the bottom). The block beside the
// profile is the glass (blue) or the panel (grey), bedded straight onto the rabbet
// it sits in.
//
// The profile drawn is the one the option needs: STD (rabbet / stop / rabbet) for
// the plain options, CO for the centred ones and SR for the two flush ones — the
// same profiles forcedProfile() applies to the cell. Proportions follow the section
// drawings in scenes/frameProfile/profiles.

const BACK = 2;      // back of the jamb (the icon's left edge)
const FACE = 7;      // the throat — the profile's face onto the opening
const STOP = 10;     // how far STD's centre stop stands into the opening
const RECESS = 5;    // SR's throat behind its single rabbet

const OUT = 3;       // outside face
const IN = 21;       // inside face
const RABBET_OUT = 7.5;  // where STD's outer rabbet meets the stop
const RABBET_IN = 16.5;  // where the stop meets STD's inner rabbet

const BLOCK_RIGHT = 22;  // the block runs off into the opening

// the block keeps the canvas's own material colours whatever state the button is in,
// so glass stays blue and a panel stays grey next to the frame; the profile fills
// with currentColor and follows the button (active / disabled) like every other
// ribbon icon.
const TONES = {
    glass: {fill: css(lineBlueGrey), stroke: css(lineBlueLight)},
    panel: {fill: css(panelMetalGrey), stroke: css(0x8C8F93)},
};

function css(color: number): string {
    return "#" + color.toString(16).padStart(6, "0");
}

type Section = {
    // the profile outline, as points down the opening-facing side.
    points: [number, number][];
    // the rabbet face the glass beds on — the block's left edge.
    bed: number;
};

// rabbet / stop / rabbet, symmetric — the profile every non-forcing option uses.
const STD: Section = {
    points: [[BACK, OUT], [FACE, OUT], [FACE, RABBET_OUT], [STOP, RABBET_OUT],
             [STOP, RABBET_IN], [FACE, RABBET_IN], [FACE, IN], [BACK, IN]],
    bed: FACE,
};

// cased open: no stop, no rabbet — a flat throat, so the glass can sit mid-depth.
const CO: Section = {
    points: [[BACK, OUT], [FACE, OUT], [FACE, IN], [BACK, IN]],
    bed: FACE,
};

// single rabbet: the throat steps back once, leaving a stop lip the glass tucks
// under. `at` is the face the rabbet opens onto.
function SR(at: "inside" | "outside"): Section {
    return {
        points: at === "inside"
            ? [[BACK, OUT], [FACE, OUT], [FACE, RABBET_IN], [RECESS, RABBET_IN], [RECESS, IN], [BACK, IN]]
            : [[BACK, OUT], [RECESS, OUT], [RECESS, RABBET_OUT], [FACE, RABBET_OUT], [FACE, IN], [BACK, IN]],
        bed: RECESS,
    };
}

// one fill icon: the section's outline, then a block per depth band.
function FillIcon(props: SvgIconProps & {section: Section; panes: [number, number][]; tone: keyof typeof TONES}) {
    const {section, panes, tone, ...rest} = props;
    const left = section.bed;

    return (
        <SvgIcon viewBox="0 0 24 24" {...rest}>
            <polygon
                points={section.points.map(p => p.join(",")).join(" ")}
                fill="currentColor" stroke="currentColor" strokeWidth={1.2} strokeLinejoin="round"
            />
            {panes.map(([top, bottom], i) => (
                <rect
                    key={i}
                    x={left} y={top} width={BLOCK_RIGHT - left} height={bottom - top}
                    fill={TONES[tone].fill} stroke={TONES[tone].stroke} strokeWidth={1.2}
                />
            ))}
        </SvgIcon>
    );
}

// the depth band a centred block occupies — the middle third of CO's throat, the
// span its section drawing anchors an item to.
const CENTERED: [number, number] = [9, 15];

export function GlazedInsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={STD} panes={[[RABBET_IN, IN]]} />;
}

export function GlazedOutsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={STD} panes={[[OUT, RABBET_OUT]]} />;
}

export function DoubleGlazedIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={STD} panes={[[OUT, RABBET_OUT], [RABBET_IN, IN]]} />;
}

export function CenterGlazedIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={CO} panes={[CENTERED]} />;
}

export function FlushGlazedInsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={SR("inside")} panes={[[RABBET_IN, IN]]} />;
}

export function FlushGlazedOutsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="glass" section={SR("outside")} panes={[[OUT, RABBET_OUT]]} />;
}

export function PanelInsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="panel" section={STD} panes={[[RABBET_IN, IN]]} />;
}

export function PanelOutsideIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="panel" section={STD} panes={[[OUT, RABBET_OUT]]} />;
}

export function PanelCenterIcon(props: SvgIconProps) {
    return <FillIcon {...props} tone="panel" section={CO} panes={[CENTERED]} />;
}
